import { useLocalStorage } from "@/helpers";
import { useGetUserTokenQuery } from "@/services";
import { useMsal } from "@azure/msal-react";
import { node } from "prop-types";
import { createContext, useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

export const UserContext = createContext();
export const useUserContext = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const intl = useIntl();
  const { instance } = useMsal();
  const account = instance.getActiveAccount();

  const [impersonationUserId, setImpersonationUserId] = useLocalStorage(
    "ImpersonationUserId",
    null
  );
  const [, storedUserToken] = useLocalStorage("userToken", undefined);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [userDisplayName, setUserDisplayName] = useState();

  const {
    refetch: refetchUserToken,
    data: userToken,
    isFetching,
    isError,
  } = useGetUserTokenQuery(impersonationUserId);

  useEffect(() => {
    if (!userToken) return;

    storedUserToken(userToken);
  }, [storedUserToken, userToken]);

  useEffect(() => {
    if (!isError) return;

    toast.error(intl.formatMessage({ id: "general.loginError" }));
  }, [intl, isError]);

  useEffect(() => {
    const oid = account?.idTokenClaims.oid;

    if (
      userToken?.user &&
      ((!impersonationUserId && userToken.user.id === oid) ||
        (impersonationUserId && impersonationUserId === userToken.user.id))
    ) {
      setIsImpersonating(
        oid ? userToken.user.id !== oid : !!impersonationUserId
      );
      return;
    }

    refetchUserToken();
  }, [
    account?.idTokenClaims.oid,
    impersonationUserId,
    refetchUserToken,
    userToken?.user,
  ]);

  useEffect(() => {
    const user = userToken?.user;

    if (!user) return;

    setUserDisplayName(user.displayName);
  }, [userToken]);

  const hasRight = (rightsToCheck) =>
    userToken?.rights &&
    rightsToCheck?.some((right) => userToken.rights.includes(right));

  const resetImpersonation = () => {
    setImpersonationUserId(null);
  };

  if (isFetching) return null;

  return (
    <UserContext.Provider
      value={{
        isUserTokenLoading: isFetching,
        impersonationUserId,
        setImpersonationUserId,
        isImpersonating,
        resetImpersonation,
        userToken,
        userDisplayName,
        hasRight,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: node.isRequired,
};
